import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  Users,
  Plus,
  Search,
  Mail,
  Phone,
  Building2,
  Tag,
  Edit,
  Trash2,
  Loader2,
  Send,
  ExternalLink,
  Clock,
  FileText,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  User,
  Upload,
  FileSpreadsheet,
  Calendar,
  UserPlus
} from "lucide-react";
import AppSidebar from "@/components/AppSidebar";
import ContactImportWizard from "@/components/ContactImportWizard";
import ContactDetailPanel from "@/components/ContactDetailPanel";
import AddToPipelineModal from "@/components/AddToPipelineModal";

// Status configs for history display
const statusConfig = {
  // Lead statuses
  new: { color: "bg-blue-100 text-blue-700", label: "New" },
  contacted: { color: "bg-yellow-100 text-yellow-700", label: "Contacted" },
  qualified: { color: "bg-purple-100 text-purple-700", label: "Qualified" },
  won: { color: "bg-green-100 text-green-700", label: "Won" },
  lost: { color: "bg-gray-100 text-gray-500", label: "Lost" },
  // Intake statuses
  waiting_for_intake: { color: "bg-amber-100 text-amber-700", label: "Waiting" },
  intake_received: { color: "bg-blue-100 text-blue-700", label: "Received" },
  awaiting_documents: { color: "bg-orange-100 text-orange-700", label: "Awaiting Docs" },
  in_review: { color: "bg-purple-100 text-purple-700", label: "In Review" },
  scheduled: { color: "bg-cyan-100 text-cyan-700", label: "Scheduled" },
  in_service: { color: "bg-indigo-100 text-indigo-700", label: "In Service" },
  completed: { color: "bg-green-100 text-green-700", label: "Completed" }
};

export default function ContactsPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  
  // State
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Selected contact for detail view
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetail, setContactDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Create/Edit modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    notes: "",
    tags: []
  });
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  
  // Add to Pipeline modal (PRIMARY ACTION)
  const [addToPipelineOpen, setAddToPipelineOpen] = useState(false);
  
  // Send Intake modal - RENAMED to Send Form (SECONDARY ACTION)
  const [sendFormModalOpen, setSendFormModalOpen] = useState(false);
  const [allForms, setAllForms] = useState([]);  // All forms (lead + intake)
  const [selectedFormId, setSelectedFormId] = useState("");
  const [formMessage, setFormMessage] = useState("");  // renamed from intakeMessage
  const [sendingForm, setSendingForm] = useState(false);  // renamed from sendingIntake
  
  // Send Email modal (SLICE 3)
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [emailPreview, setEmailPreview] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Import Wizard
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  
  // Tag management
  const [newTagInput, setNewTagInput] = useState("");
  
  // Activity Timeline state
  const [activityTimeline, setActivityTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [expandedActivities, setExpandedActivities] = useState(new Set());  // Track expanded items
  
  // Multi-select for bulk actions
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [bulkSendModalOpen, setBulkSendModalOpen] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);

  useEffect(() => {
    fetchContacts();
    fetchAllForms();  // renamed from fetchPostBookingForms
    fetchEmailTemplates();
  }, []);

  const fetchContacts = async (search = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/contacts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContacts(response.data.contacts || []);
      setTotalContacts(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllForms = async () => {
    try {
      const response = await axios.get(`${API}/business/forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Get all forms (both lead and intake)
      const forms = response.data.forms || [];
      setAllForms(forms);
    } catch (error) {
      console.error("Failed to fetch forms");
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/workflow/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch email templates");
    }
  };

  const fetchContactDetail = async (contactId) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(`${API}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactDetail(response.data);
      
      // Also fetch activity timeline
      fetchActivityTimeline(contactId);
    } catch (error) {
      toast.error("Failed to load contact details");
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchActivityTimeline = async (contactId) => {
    try {
      setLoadingTimeline(true);
      const response = await axios.get(`${API}/contacts/${contactId}/activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityTimeline(response.data.activities || []);
    } catch (error) {
      console.error("Failed to fetch activity timeline");
      setActivityTimeline([]);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const handleSearch = () => {
    fetchContacts(searchQuery);
  };

  const handleCreateContact = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    setSaving(true);
    try {
      if (editMode && selectedContact) {
        // Update
        await axios.put(`${API}/contacts/${selectedContact.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Contact updated");
      } else {
        // Create
        await axios.post(`${API}/contacts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Contact created");
      }
      
      setCreateModalOpen(false);
      resetForm();
      fetchContacts(searchQuery);
      
      // Refresh detail if viewing
      if (selectedContact) {
        fetchContactDetail(selectedContact.id);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to save contact";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete) return;
    
    try {
      await axios.delete(`${API}/contacts/${contactToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contact deleted");
      setDeleteDialogOpen(false);
      setContactToDelete(null);
      
      // Close detail view if deleted contact was being viewed
      if (selectedContact?.id === contactToDelete.id) {
        setSelectedContact(null);
        setContactDetail(null);
      }
      
      fetchContacts(searchQuery);
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  // Tag management functions
  const addTagToContact = async (contactId, tag) => {
    if (!tag.trim()) return;
    
    try {
      const contact = contacts.find(c => c.id === contactId) || contactDetail?.contact;
      const currentTags = contact?.tags || [];
      const normalizedTag = tag.trim().toLowerCase();
      
      if (currentTags.includes(normalizedTag)) {
        toast.error("Tag already exists");
        return;
      }
      
      const updatedTags = [...currentTags, normalizedTag];
      
      await axios.put(`${API}/contacts/${contactId}`, {
        ...contact,
        tags: updatedTags
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh contact detail and list
      fetchContactDetail(contactId);
      fetchContacts(searchQuery);
      setNewTagInput("");
      toast.success("Tag added");
    } catch (error) {
      toast.error("Failed to add tag");
    }
  };

  const removeTagFromContact = async (contactId, tagToRemove) => {
    try {
      const contact = contacts.find(c => c.id === contactId) || contactDetail?.contact;
      const currentTags = contact?.tags || [];
      const updatedTags = currentTags.filter(t => t !== tagToRemove);
      
      await axios.put(`${API}/contacts/${contactId}`, {
        ...contact,
        tags: updatedTags
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh contact detail and list
      fetchContactDetail(contactId);
      fetchContacts(searchQuery);
      toast.success("Tag removed");
    } catch (error) {
      toast.error("Failed to remove tag");
    }
  };

  const handleSendForm = async () => {
    if (!selectedFormId) {
      toast.error("Please select a form");
      return;
    }
    
    if (!contactDetail?.contact?.email) {
      toast.error("Contact has no email address");
      return;
    }
    
    setSendingForm(true);
    try {
      await axios.post(`${API}/submissions/send-intake`, {
        form_id: selectedFormId,
        contact_id: contactDetail.contact.id,
        message: formMessage || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Form sent successfully");
      setSendFormModalOpen(false);
      setSelectedFormId("");
      setFormMessage("");
      
      // Refresh contact detail and activity timeline
      fetchContactDetail(contactDetail.contact.id);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send form";
      toast.error(message);
    } finally {
      setSendingForm(false);
    }
  };

  const handleSelectTemplate = async (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId || !contactDetail?.contact?.id) return;
    
    try {
      const response = await axios.post(`${API}/workflow/preview`, {
        template_id: templateId,
        contact_id: contactDetail.contact.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailPreview(response.data);
    } catch (error) {
      console.error("Failed to preview template");
    }
  };

  const handleSendEmail = async () => {
    if (!selectedTemplateId) {
      toast.error("Please select a template");
      return;
    }
    if (!contactDetail?.contact?.id) {
      toast.error("No contact selected");
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/workflow/email/send`, {
        contact_id: contactDetail.contact.id,
        template_id: selectedTemplateId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Email sent successfully");
      setSendEmailModalOpen(false);
      setSelectedTemplateId("");
      setEmailPreview(null);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send email";
      toast.error(message);
    } finally {
      setSendingEmail(false);
    }
  };

  // Multi-select handlers
  const toggleContactSelection = (contactId) => {
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedContactIds.size === contacts.length) {
      setSelectedContactIds(new Set());
    } else {
      setSelectedContactIds(new Set(contacts.map(c => c.id)));
    }
  };

  const handleBulkSendForm = async () => {
    if (!selectedFormId) {
      toast.error("Please select a form");
      return;
    }
    
    if (selectedContactIds.size === 0) {
      toast.error("No contacts selected");
      return;
    }
    
    setBulkSending(true);
    try {
      const response = await axios.post(`${API}/submissions/bulk-send-intake`, {
        contact_ids: Array.from(selectedContactIds),
        form_id: selectedFormId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      let message = `Created ${result.created} form request${result.created !== 1 ? 's' : ''}`;
      if (result.skipped_existing_pending > 0) {
        message += `, ${result.skipped_existing_pending} skipped (pending request exists)`;
      }
      
      toast.success(message);
      setBulkSendModalOpen(false);
      setSelectedFormId("");
      setSelectedContactIds(new Set());
      fetchContacts(searchQuery);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send bulk forms";
      toast.error(message);
    } finally {
      setBulkSending(false);
    }
  };

  const handleImportComplete = () => {
    fetchContacts(searchQuery);
    setSelectedContactIds(new Set());
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      company: "",
      notes: "",
      tags: []
    });
    setEditMode(false);
  };

  const openEditModal = (contact) => {
    setFormData({
      name: contact.name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      company: contact.company || "",
      notes: contact.notes || "",
      tags: contact.tags || []
    });
    setEditMode(true);
    setSelectedContact(contact);
    setCreateModalOpen(true);
  };

  const openDetailView = (contact) => {
    setSelectedContact(contact);
    fetchContactDetail(contact.id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="contacts-page">
      <AppSidebar user={user} onLogout={logout} />
      
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold" data-testid="contacts-title">Contacts</h1>
              <p className="text-muted-foreground mt-1">Manage your client directory</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setImportWizardOpen(true)}
                className="rounded-xl"
                data-testid="import-contacts-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Button 
                onClick={() => { resetForm(); setCreateModalOpen(true); }}
                className="rounded-xl"
                data-testid="create-contact-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Contacts List */}
            <div className="lg:col-span-2">
              {/* Search */}
              <Card className="rounded-2xl border-none shadow-sm mb-6">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        className="pl-10 rounded-xl"
                        data-testid="search-contacts-input"
                      />
                    </div>
                    <Button onClick={handleSearch} className="rounded-xl">
                      Search
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Contacts Table */}
              <Card className="rounded-2xl border-none shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg">All Contacts ({totalContacts})</h3>
                    
                    {/* Bulk Actions */}
                    {selectedContactIds.size > 0 && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">
                          {selectedContactIds.size} selected
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="rounded-lg">
                              Bulk Actions
                              <ChevronDown className="w-4 h-4 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => setBulkSendModalOpen(true)}
                              disabled={allForms.length === 0}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Send Intake to Selected
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setSelectedContactIds(new Set())}
                        >
                          Clear Selection
                        </Button>
                      </div>
                    )}
                  </div>

                  {loading ? (
                    <div className="flex items-center justify-center h-48">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium mb-2">No contacts yet</h3>
                      <p className="text-muted-foreground mb-4">Add your first contact to get started</p>
                      <Button onClick={() => { resetForm(); setCreateModalOpen(true); }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Contact
                      </Button>
                    </div>
                  ) : (
                    <Table data-testid="contacts-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox 
                              checked={contacts.length > 0 && selectedContactIds.size === contacts.length}
                              onCheckedChange={toggleSelectAll}
                              aria-label="Select all"
                            />
                          </TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Tags</TableHead>
                          <TableHead>Updated</TableHead>
                          <TableHead className="w-24">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contacts.map((contact) => (
                          <TableRow 
                            key={contact.id} 
                            className={`cursor-pointer hover:bg-muted/50 ${selectedContactIds.has(contact.id) ? 'bg-primary/5' : ''}`}
                            data-testid={`contact-row-${contact.id}`}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox 
                                checked={selectedContactIds.has(contact.id)}
                                onCheckedChange={() => toggleContactSelection(contact.id)}
                                aria-label={`Select ${contact.name}`}
                              />
                            </TableCell>
                            <TableCell 
                              className="font-medium"
                              onClick={() => openDetailView(contact)}
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <User className="w-4 h-4 text-primary" />
                                </div>
                                {contact.name || "—"}
                              </div>
                            </TableCell>
                            <TableCell 
                              className="text-muted-foreground"
                              onClick={() => openDetailView(contact)}
                            >
                              {contact.email || "—"}
                            </TableCell>
                            <TableCell 
                              className="text-muted-foreground"
                              onClick={() => openDetailView(contact)}
                            >
                              {contact.phone || "—"}
                            </TableCell>
                            <TableCell onClick={() => openDetailView(contact)}>
                              <div className="flex gap-1">
                                {(contact.tags || []).slice(0, 2).map((tag) => (
                                  <Badge key={tag} variant="outline" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                                {(contact.tags?.length || 0) > 2 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{contact.tags.length - 2}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {formatDate(contact.updated_at || contact.created_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditModal(contact)}
                                  data-testid={`edit-contact-${contact.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => {
                                    setContactToDelete(contact);
                                    setDeleteDialogOpen(true);
                                  }}
                                  data-testid={`delete-contact-${contact.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Detail Panel */}
            <div className="lg:col-span-1">
              <Card className="rounded-2xl border-none shadow-sm sticky top-8">
                <CardContent className="p-6">
                  {!selectedContact ? (
                    <div className="text-center py-12">
                      <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Select a contact to view details</p>
                    </div>
                  ) : detailLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : contactDetail ? (
                    <div className="space-y-6">
                      {/* Contact Header */}
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                          <User className="w-8 h-8 text-primary" />
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <h3 className="text-xl font-bold">{contactDetail.contact.name}</h3>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-full hover:bg-slate-100"
                            onClick={() => openEditModal(contactDetail.contact)}
                            data-testid="edit-contact-btn"
                          >
                            <Edit className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        </div>
                        {contactDetail.contact.company && (
                          <p className="text-muted-foreground flex items-center justify-center gap-1 mt-1">
                            <Building2 className="w-4 h-4" />
                            {contactDetail.contact.company}
                          </p>
                        )}
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-3">
                        {contactDetail.contact.email && (
                          <div className="flex items-center gap-3 text-sm">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <span>{contactDetail.contact.email}</span>
                          </div>
                        )}
                        {contactDetail.contact.phone && (
                          <div className="flex items-center gap-3 text-sm">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span>{contactDetail.contact.phone}</span>
                          </div>
                        )}
                      </div>

                      {/* Tags */}
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Tags</p>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {contactDetail.contact.tags?.length > 0 ? (
                            contactDetail.contact.tags.map((tag) => (
                              <Badge 
                                key={tag} 
                                variant="outline" 
                                className="cursor-pointer hover:bg-destructive/10 group"
                                onClick={() => removeTagFromContact(contactDetail.contact.id, tag)}
                              >
                                {tag}
                                <span className="ml-1 text-muted-foreground group-hover:text-destructive">×</span>
                              </Badge>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground italic">No tags</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Add a tag..."
                            value={newTagInput}
                            onChange={(e) => setNewTagInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addTagToContact(contactDetail.contact.id, newTagInput);
                              }
                            }}
                            className="h-8 text-sm"
                            data-testid="add-tag-input"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => addTagToContact(contactDetail.contact.id, newTagInput)}
                            disabled={!newTagInput.trim()}
                            data-testid="add-tag-btn"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Notes */}
                      {contactDetail.contact.notes && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Notes</p>
                          <p className="text-sm bg-muted/50 rounded-lg p-3">
                            {contactDetail.contact.notes}
                          </p>
                        </div>
                      )}

                      {/* Quick Actions - Suggested next steps */}
                      {(() => {
                        const hasEmails = activityTimeline.some(a => a.type === "email_sent");
                        const latestSubmission = contactDetail.submissions?.[0];
                        const hasIntake = contactDetail.submissions?.some(s => s.mode === "post_booking");
                        const hasSchedulingLink = activityTimeline.some(a => 
                          a.type === "email_sent" && 
                          a.data?.subject?.toLowerCase().includes("schedule")
                        );
                        
                        // Only show suggestions if contact has no submissions yet
                        if (contactDetail.submissions?.length > 0) return null;
                        
                        let suggestion = null;
                        let suggestionIcon = null;
                        let suggestionAction = null;
                        
                        if (!hasEmails) {
                          suggestion = "Send welcome email";
                          suggestionIcon = <Mail className="w-4 h-4" />;
                          suggestionAction = () => setSendEmailModalOpen(true);
                        }
                        
                        if (!suggestion) return null;
                        
                        return (
                          <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border">
                            <p className="text-xs text-muted-foreground mb-2">Suggested next action</p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full justify-start text-primary border-primary/30 hover:bg-primary/5"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                suggestionAction?.();
                              }}
                              data-testid="suggested-action-btn"
                            >
                              {suggestionIcon}
                              <span className="ml-2">{suggestion}</span>
                            </Button>
                          </div>
                        );
                      })()}

                      {/* Actions */}
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          type="button"
                          className="flex-1 rounded-xl"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log("Add to Pipeline clicked, opening modal");
                            setAddToPipelineOpen(true);
                          }}
                          disabled={!contactDetail?.contact?.email}
                          data-testid="add-to-pipeline-btn"
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Add to Pipeline
                        </Button>
                        <Button
                          type="button"
                          className="flex-1 rounded-xl"
                          variant="outline"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSendEmailModalOpen(true);
                          }}
                          disabled={!contactDetail?.contact?.email || emailTemplates.length === 0}
                          data-testid="send-email-btn"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Send Email
                        </Button>
                      </div>

                      {/* Activity Timeline */}
                      <div data-testid="activity-timeline">
                        <p className="text-sm font-medium mb-3">Activity Timeline</p>
                        {loadingTimeline ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                          </div>
                        ) : activityTimeline.length > 0 ? (
                          <div className="space-y-3 max-h-[500px] overflow-y-auto">
                            {activityTimeline.map((activity, index) => {
                              const formatActivityDate = (timestamp) => {
                                if (!timestamp) return "";
                                const date = new Date(timestamp);
                                return date.toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  hour: "numeric",
                                  minute: "2-digit"
                                });
                              };
                              
                              const activityKey = activity.id || `${activity.type}-${index}`;
                              const isExpanded = expandedActivities.has(activityKey);
                              
                              const toggleExpand = (e) => {
                                e.stopPropagation();
                                setExpandedActivities(prev => {
                                  const next = new Set(prev);
                                  if (next.has(activityKey)) {
                                    next.delete(activityKey);
                                  } else {
                                    next.add(activityKey);
                                  }
                                  return next;
                                });
                              };
                              
                              // Email Event - Expandable
                              if (activity.type === "email_sent" || activity.type === "email_failed") {
                                const isFailed = activity.type === "email_failed" || activity.data?.status === "failed";
                                const statusColor = isFailed ? "text-red-600" : "text-green-600";
                                const statusIcon = isFailed ? <AlertCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />;
                                const headerBg = isFailed ? "bg-red-50/50" : "bg-blue-50/50";
                                const headerBorder = isFailed ? "border-red-100" : "border-blue-100";
                                const headerHover = isFailed ? "hover:bg-red-50" : "hover:bg-blue-50";
                                const iconBg = isFailed ? "bg-red-100" : "bg-blue-100";
                                const iconColor = isFailed ? "text-red-600" : "text-blue-600";
                                const titleColor = isFailed ? "text-red-800" : "text-blue-800";
                                const subjectColor = isFailed ? "text-red-600" : "text-blue-600";
                                const chevronColor = isFailed ? "text-red-400" : "text-blue-400";
                                
                                return (
                                  <div 
                                    key={activityKey}
                                    className={`${headerBg} rounded-lg border ${headerBorder} overflow-hidden`}
                                    data-testid={`activity-email-${activity.id}`}
                                  >
                                    {/* Collapsed Header */}
                                    <div 
                                      className={`flex items-start gap-3 p-3 cursor-pointer ${headerHover}`}
                                      onClick={toggleExpand}
                                    >
                                      <div className={`p-1.5 rounded-full ${iconBg}`}>
                                        <Mail className={`w-3.5 h-3.5 ${iconColor}`} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <p className={`text-sm font-medium ${titleColor}`}>
                                            {isFailed ? "Email Failed" : "Email Sent"}
                                          </p>
                                          <span className={`flex items-center gap-1 text-xs ${statusColor}`}>
                                            {statusIcon}
                                            {isFailed ? "failed" : (activity.data?.status || "sent")}
                                          </span>
                                        </div>
                                        <p className={`text-xs ${subjectColor} truncate`}>{activity.data?.subject}</p>
                                        <p className="text-xs text-muted-foreground mt-1">{formatActivityDate(activity.timestamp)}</p>
                                      </div>
                                      <ChevronDown className={`w-4 h-4 ${chevronColor} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>
                                    
                                    {/* Expanded Content */}
                                    {isExpanded && (
                                      <div className={`px-4 pb-4 pt-0 border-t ${headerBorder} bg-white`}>
                                        <div className="space-y-3 mt-3">
                                          <div>
                                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Subject</p>
                                            <p className="text-sm mt-1">{activity.data?.subject}</p>
                                          </div>
                                          {isFailed && activity.data?.failure_reason && (
                                            <div>
                                              <p className="text-xs font-medium text-red-600 uppercase tracking-wide">Failure Reason</p>
                                              <p className="text-sm mt-1 text-red-700">{activity.data?.failure_reason}</p>
                                            </div>
                                          )}
                                          {!isFailed && activity.data?.body && (
                                            <div>
                                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Body</p>
                                              <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                                                {activity.data?.body || "No body content"}
                                              </div>
                                            </div>
                                          )}
                                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                            <span>To: {activity.data?.recipient_email}</span>
                                            {activity.data?.reply_to && (
                                              <span>Reply-to: {activity.data?.reply_to}</span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                                              isFailed ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                                            }`}>
                                              {statusIcon}
                                              Delivery: {isFailed ? "failed" : (activity.data?.status || "sent")}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              
                              // Submission Event - Expandable
                              if (activity.type === "submission_received") {
                                const isIntake = activity.data?.mode === "post_booking";
                                const formData = activity.data?.form_data || {};
                                const formDataEntries = Object.entries(formData);
                                
                                return (
                                  <div 
                                    key={activityKey}
                                    className={`rounded-lg border overflow-hidden ${
                                      isIntake ? 'bg-purple-50/50 border-purple-100' : 'bg-green-50/50 border-green-100'
                                    }`}
                                    data-testid={`activity-submission-${activity.data?.submission_id}`}
                                  >
                                    {/* Collapsed Header */}
                                    <div 
                                      className={`flex items-start gap-3 p-3 cursor-pointer ${
                                        isIntake ? 'hover:bg-purple-50' : 'hover:bg-green-50'
                                      }`}
                                      onClick={toggleExpand}
                                    >
                                      <div className={`p-1.5 rounded-full ${isIntake ? 'bg-purple-100' : 'bg-green-100'}`}>
                                        <FileText className={`w-3.5 h-3.5 ${isIntake ? 'text-purple-600' : 'text-green-600'}`} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium ${isIntake ? 'text-purple-800' : 'text-green-800'}`}>
                                          {isIntake ? 'Intake Submitted' : 'Lead Received'}
                                        </p>
                                        <p className={`text-xs ${isIntake ? 'text-purple-600' : 'text-green-600'}`}>
                                          {activity.data?.form_name}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">{formatActivityDate(activity.timestamp)}</p>
                                      </div>
                                      <ChevronDown className={`w-4 h-4 ${isIntake ? 'text-purple-400' : 'text-green-400'} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>
                                    
                                    {/* Expanded Content */}
                                    {isExpanded && (
                                      <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-white">
                                        <div className="space-y-3 mt-3">
                                          <div>
                                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Submitted Data</p>
                                            {formDataEntries.length > 0 ? (
                                              <div className="mt-2 space-y-2">
                                                {formDataEntries.map(([key, value], i) => (
                                                  <div key={i} className="flex items-start gap-2 text-sm">
                                                    <span className="font-medium text-muted-foreground min-w-[100px]">{key}:</span>
                                                    <span className="flex-1 break-words">
                                                      {typeof value === 'object' ? JSON.stringify(value) : String(value || '-')}
                                                    </span>
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <p className="text-sm text-muted-foreground mt-1">No form data available</p>
                                            )}
                                          </div>
                                          {activity.data?.customer_email && (
                                            <div className="text-xs text-muted-foreground pt-2 border-t">
                                              Submitted by: {activity.data?.customer_name || 'Unknown'} ({activity.data?.customer_email})
                                            </div>
                                          )}
                                          {/* Only show View Full Submission if submission_id exists */}
                                          {activity.data?.submission_id && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="w-full mt-2"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/submissions?id=${activity.data.submission_id}`);
                                              }}
                                            >
                                              View Full Submission
                                              <ChevronRight className="w-4 h-4 ml-2" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              
                              // Automation Event - Expandable
                              if (activity.type === "automation_executed") {
                                const isSuccess = activity.data?.status === "success";
                                const actionsExecuted = activity.data?.actions_executed || [];
                                
                                return (
                                  <div 
                                    key={activityKey}
                                    className={`rounded-lg border overflow-hidden ${
                                      isSuccess ? 'bg-indigo-50/50 border-indigo-100' : 'bg-red-50/50 border-red-100'
                                    }`}
                                    data-testid={`activity-automation-${activity.id}`}
                                  >
                                    {/* Collapsed Header */}
                                    <div 
                                      className={`flex items-start gap-3 p-3 cursor-pointer ${
                                        isSuccess ? 'hover:bg-indigo-50' : 'hover:bg-red-50'
                                      }`}
                                      onClick={toggleExpand}
                                    >
                                      <div className={`p-1.5 rounded-full ${isSuccess ? 'bg-indigo-100' : 'bg-red-100'}`}>
                                        <Clock className={`w-3.5 h-3.5 ${isSuccess ? 'text-indigo-600' : 'text-red-600'}`} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <p className={`text-sm font-medium ${isSuccess ? 'text-indigo-800' : 'text-red-800'}`}>
                                            Automation Ran
                                          </p>
                                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                                            isSuccess ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                          }`}>
                                            {activity.data?.status}
                                          </span>
                                        </div>
                                        <p className={`text-xs ${isSuccess ? 'text-indigo-600' : 'text-red-600'}`}>
                                          {activity.data?.automation_name} ({activity.data?.actions_count} action{activity.data?.actions_count !== 1 ? 's' : ''})
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">{formatActivityDate(activity.timestamp)}</p>
                                      </div>
                                      <ChevronDown className={`w-4 h-4 ${isSuccess ? 'text-indigo-400' : 'text-red-400'} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>
                                    
                                    {/* Expanded Content - Actions Executed */}
                                    {isExpanded && (
                                      <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-white">
                                        <div className="space-y-3 mt-3">
                                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions Executed</p>
                                          {actionsExecuted.length > 0 ? (
                                            <div className="space-y-2">
                                              {actionsExecuted.map((action, actionIndex) => {
                                                const actionSuccess = action.status === "success";
                                                return (
                                                  <div 
                                                    key={actionIndex}
                                                    className={`p-3 rounded-lg border ${
                                                      actionSuccess ? 'bg-green-50/30 border-green-100' : 'bg-red-50/30 border-red-100'
                                                    }`}
                                                  >
                                                    <div className="flex items-center justify-between">
                                                      <div className="flex items-center gap-2">
                                                        <span className={`w-2 h-2 rounded-full ${actionSuccess ? 'bg-green-500' : 'bg-red-500'}`} />
                                                        <span className="text-sm font-medium capitalize">
                                                          {action.type?.replace(/_/g, ' ')}
                                                        </span>
                                                      </div>
                                                      <span className={`text-xs ${actionSuccess ? 'text-green-600' : 'text-red-600'}`}>
                                                        {actionSuccess ? 'Success' : 'Failed'}
                                                      </span>
                                                    </div>
                                                    {action.timestamp && (
                                                      <p className="text-xs text-muted-foreground mt-1">
                                                        {formatActivityDate(action.timestamp)}
                                                      </p>
                                                    )}
                                                    {action.error && (
                                                      <p className="text-xs text-red-600 mt-1">
                                                        Error: {action.error}
                                                      </p>
                                                    )}
                                                    {action.details && Object.keys(action.details).length > 0 && (
                                                      <div className="mt-2 text-xs text-muted-foreground">
                                                        {Object.entries(action.details).map(([k, v]) => (
                                                          <div key={k}>{k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            <p className="text-sm text-muted-foreground">No action details available</p>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              
                              // Status Changed Event (non-expandable - simple)
                              if (activity.type === "status_changed") {
                                const statusLabels = { ...statusConfig };
                                const toLabel = statusLabels[activity.data?.to_status]?.label || activity.data?.to_status;
                                return (
                                  <div 
                                    key={activityKey}
                                    className="flex items-start gap-3 p-3 bg-amber-50/50 rounded-lg border border-amber-100"
                                    data-testid={`activity-status-${index}`}
                                  >
                                    <div className="p-1.5 rounded-full bg-amber-100">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-amber-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-amber-800">Status Changed</p>
                                      <p className="text-xs text-amber-600">
                                        {activity.data?.from_status && `${activity.data.from_status} → `}{toLabel}
                                      </p>
                                      <p className="text-xs text-muted-foreground mt-1">{formatActivityDate(activity.timestamp)}</p>
                                    </div>
                                  </div>
                                );
                              }
                              
                              // Tag Update Event (non-expandable - simple)
                              if (activity.type === "tag_update") {
                                return (
                                  <div 
                                    key={activityKey}
                                    className="flex items-start gap-3 p-3 bg-gray-50/50 rounded-lg border border-gray-100"
                                    data-testid={`activity-tag-${index}`}
                                  >
                                    <div className="p-1.5 rounded-full bg-gray-100">
                                      <Tag className="w-3.5 h-3.5 text-gray-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-800">Tag Updated</p>
                                      <p className="text-xs text-gray-600">
                                        {activity.data?.action === "tag_added" ? "Added: " : "Removed: "}
                                        {activity.data?.tag}
                                      </p>
                                      <p className="text-xs text-muted-foreground mt-1">{formatActivityDate(activity.timestamp)}</p>
                                    </div>
                                  </div>
                                );
                              }
                              
                              // Fallback for unknown types
                              return (
                                <div 
                                  key={activityKey}
                                  className="flex items-start gap-3 p-3 bg-gray-50/50 rounded-lg border"
                                >
                                  <div className="p-1.5 rounded-full bg-gray-100">
                                    <Clock className="w-3.5 h-3.5 text-gray-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium">{activity.type}</p>
                                    <p className="text-xs text-muted-foreground mt-1">{formatActivityDate(activity.timestamp)}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-6 space-y-4" data-testid="empty-activity-state">
                            <div className="w-12 h-12 mx-auto rounded-full bg-muted/50 flex items-center justify-center">
                              <Clock className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">No activity yet</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Start engaging with this contact
                              </p>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={() => setSendEmailModalOpen(true)}
                                disabled={!contactDetail?.contact?.email}
                                data-testid="empty-state-send-email"
                              >
                                <Mail className="w-4 h-4 mr-2" />
                                Send welcome email
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={() => setSendFormModalOpen(true)}
                                disabled={!contactDetail?.contact?.email || allForms.length === 0}
                                data-testid="empty-state-send-form"
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Send a form
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit Contact" : "Add Contact"}</DialogTitle>
            <DialogDescription>
              {editMode ? "Update contact information" : "Add a new contact to your directory"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                placeholder="+1 (555) 123-4567"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <Label>Company</Label>
              <Input
                placeholder="Acme Corp"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                data-testid="contact-company-input"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Any notes about this contact..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="contact-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateContact} disabled={saving} data-testid="save-contact-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editMode ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{contactToDelete?.name}". Linked submissions will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteContact}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Form Modal */}
      <Dialog open={sendFormModalOpen} onOpenChange={setSendFormModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Form</DialogTitle>
            <DialogDescription>
              Send a form to {contactDetail?.contact?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {allForms.length === 0 ? (
              <div className="text-center py-6">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground mb-3">No forms available</p>
                <Button onClick={() => navigate("/form-builder")}>
                  Create Form
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <Label>Select Form *</Label>
                  <Select value={selectedFormId} onValueChange={setSelectedFormId}>
                    <SelectTrigger data-testid="select-form-dropdown">
                      <SelectValue placeholder="Choose a form" />
                    </SelectTrigger>
                    <SelectContent>
                      {allForms.map((form) => (
                        <SelectItem key={form.id} value={form.id}>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${form.mode === "post_booking" ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}
                            >
                              {form.mode === "post_booking" ? "Intake" : "Lead"}
                            </Badge>
                            {form.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Custom Message (optional)</Label>
                  <Textarea
                    placeholder="Add a personal message to include in the email..."
                    value={formMessage}
                    onChange={(e) => setFormMessage(e.target.value)}
                    rows={3}
                    data-testid="form-message-input"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendFormModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSendForm} 
              disabled={sendingForm || !selectedFormId}
              data-testid="confirm-send-form-btn"
            >
              {sendingForm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Send Form Modal */}
      <Dialog open={bulkSendModalOpen} onOpenChange={setBulkSendModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Bulk Send Form
            </DialogTitle>
            <DialogDescription>
              Send forms to {selectedContactIds.size} selected contact{selectedContactIds.size !== 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {allForms.length === 0 ? (
              <div className="text-center py-6">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground mb-3">No forms available</p>
                <Button onClick={() => navigate("/form-builder")}>
                  Create Form
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <Label>Select Form *</Label>
                  <Select value={selectedFormId} onValueChange={setSelectedFormId}>
                    <SelectTrigger data-testid="bulk-select-form-dropdown">
                      <SelectValue placeholder="Choose a form" />
                    </SelectTrigger>
                    <SelectContent>
                      {allForms.map((form) => (
                        <SelectItem key={form.id} value={form.id}>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${form.mode === "post_booking" ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}
                            >
                              {form.mode === "post_booking" ? "Intake" : "Lead"}
                            </Badge>
                            {form.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  Contacts with an existing pending form request will be skipped automatically.
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkSendModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkSendForm} 
              disabled={bulkSending || !selectedFormId || selectedContactIds.size === 0}
              data-testid="confirm-bulk-send-btn"
            >
              {bulkSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send to {selectedContactIds.size} Contact{selectedContactIds.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Import Wizard */}
      <ContactImportWizard 
        open={importWizardOpen}
        onClose={() => setImportWizardOpen(false)}
        token={token}
        onImportComplete={handleImportComplete}
      />

      {/* Send Email Modal (SLICE 3) */}
      <Dialog open={sendEmailModalOpen} onOpenChange={setSendEmailModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Send Email
            </DialogTitle>
            <DialogDescription>
              Send an email to {contactDetail?.contact?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {emailTemplates.length === 0 ? (
              <div className="text-center py-6">
                <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground mb-3">No email templates available</p>
                <Button onClick={() => navigate("/email-templates")}>
                  Create Template
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <Label>Select Template *</Label>
                  <Select value={selectedTemplateId} onValueChange={handleSelectTemplate}>
                    <SelectTrigger data-testid="select-email-template">
                      <SelectValue placeholder="Choose a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {emailPreview && (
                  <div className="space-y-3">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Subject</p>
                      <p className="font-medium">{emailPreview.subject}</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 max-h-48 overflow-auto">
                      <p className="text-sm font-medium text-muted-foreground mb-2">Body Preview</p>
                      <div className="whitespace-pre-wrap text-sm">{emailPreview.body}</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendEmailModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSendEmail} 
              disabled={sendingEmail || !selectedTemplateId}
              data-testid="confirm-send-email-btn"
            >
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add to Pipeline Modal */}
      <AddToPipelineModal
        open={addToPipelineOpen}
        onOpenChange={setAddToPipelineOpen}
        contact={contactDetail?.contact || selectedContact}
        token={token}
        intakeForms={allForms.filter(f => f.mode === "post_booking")}
        leadForms={allForms.filter(f => f.mode === "pre_booking")}
        onSuccess={(submission) => {
          // Refresh contact detail and timeline
          const contactId = contactDetail?.contact?.id || selectedContact?.id;
          if (contactId) {
            fetchContactDetail(contactId);
          }
          fetchContacts();
        }}
      />
    </div>
  );
}
